import React, { lazy, Suspense, useState, memo } from "react";
import { Container, Row, Col, Button} from "react-bootstrap";
import HTMLReactParser from "html-react-parser";
import Header from "../Header";
import "../Body/Body.css";
import "./LoanOfficerBanner.css";
import Helmet from "react-helmet";

// Lazy load widget script
const WidgetScript = lazy(() => import("../ScriptTags/widget"));

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";

  // Extract digits and format as (XXX) XXX-XXXX
  const cleaned = phoneNumber.replace(/\D/g, ""); // Remove all non-numeric characters
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
};

// Memoized component for static rendering optimization
const LoanOfficerDetails = memo(({ details, mapLink }) => (
  <div className="loanofficer-details">
    {details.name && <h4>{HTMLReactParser(details.name)}</h4>}
    {details.nmls && <p>NMLS {HTMLReactParser(details.nmls)}</p>}
    {details.designation && <p>{HTMLReactParser(details.designation)}</p>}
    {details.workPhoneNumber && (
      <p>
        D:{" "}
        <a
          href={`tel:+1${details.workPhoneNumber}`}
          target="_blank"
          rel="noreferrer"
        >
          {formatPhoneNumber(details.workPhoneNumber)}
        </a>
      </p>
    )}

    {details.emailAddress && (
      <p>
        <a href={`mailto:${details.emailAddress}`}>
          {HTMLReactParser(details.emailAddress)}
        </a>
      </p>
    )}
    {details.userWebsite && (
      <p>
        <a
          href={`https://${details.userWebsite}`}
          target="_blank"
          rel="noreferrer"
        >
          {HTMLReactParser(details.userWebsite)}
        </a>
      </p>
    )}
    {(details.streetAddress || details.city || details.state || details.zipCode) && (
      <p>
        <a href={mapLink} target="_blank" rel="noreferrer">
          {details.streetAddress ? HTMLReactParser(details.streetAddress) : ""}
          <br />
          {details.city ? HTMLReactParser(details.city) : ""},{" "}
          {details.state ? HTMLReactParser(details.state) : ""},{" "}
          {details.zipCode ? HTMLReactParser(details.zipCode) : ""}
        </a>
      </p>
    )}
    {details.branchNmls && (
      <p>Branch NMLS: {HTMLReactParser(details.branchNmls)}</p>
    )}
  </div>
));

// Main LoanOfficerBanner component
export default function LoanOfficerBanner({ links, loanOfficerData }) {
  const [play, setPlay] = useState(false);
  const [url, setUrl] = useState("");

  const loanOfficerDetails = loanOfficerData?.loanOfficer || {};
  const profileImageUrl = loanOfficerDetails.profileImage;

  // Construct dynamic map link based on address
  const mapLink = loanOfficerData
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${loanOfficerDetails.streetAddress || ""}, ${
          loanOfficerDetails.city || ""
        }, ${loanOfficerDetails.state || ""} ${
          loanOfficerDetails.zipCode || ""
        }`
      )}` 
    : "#";

  const handleVideoToggle = (newUrl) => {
    setPlay(!play);
    setUrl(newUrl);
  };

  return (
    <>
      <Helmet>
        {/* Preload the profile image */}
        {profileImageUrl && (
          <link rel="preload" as="image" href={profileImageUrl} />
        )}
      </Helmet>
      <div className="banner banner-lo">
        <Header links={links} />
        <Container>
          <Row>
            <Col sm={12} md={12} lg={6} xl={6}>
              <div className="LObannertext">
                <h2 className="d-none d-lg-block d-md-none" id="LObannertext">
                  <span className="orange">Free Lead Prospecting</span>
                  <br />
                  and Search Engine
                  <br />
                  Boost Driven by
                </h2>
                <h2 className="brandName">{links.current.brandName}</h2>
                <h2 className="d-lg-none">
                  <span className="orange">Free Lead Prospecting</span>
                  <br />
                  and Search Engine
                  <br />
                  Boost Driven by
                </h2>
                <h2 className="brandName-mob">{links.current.brandName}</h2>
                <p className="close-fast">
                  Close Fast <span>|</span> Earn More
                </p>
              </div>
            </Col>
            <Col sm={12} md={12} lg={6} xl={6} className="loanofficer-section">
              <div className="sky-div">
                <p className="sky-text">
                  The Sky's the
                  <br />
                  Limit With Ai
                  <br />
                  by Your Side
                </p>
              </div>
              <div className="loanofficer-image-section">
                <img
                  src="https://resources.swmc.com/workshop/lo-img-bg-desk.webp"
                  alt={loanOfficerDetails.name || ""}
                  className="loanofficer-img-bg"
                  loading="eager"
                />
                {profileImageUrl && (
                  <img
                    src={profileImageUrl}
                    alt={loanOfficerDetails.name || ""}
                    className="loanofficer-img"
                    loading="eager"
                  />
                )}
              </div>
              <LoanOfficerDetails details={loanOfficerDetails} mapLink={mapLink} />
            </Col>
            <div className="SignupBtn-div">
              <Button
                className="SignupBtn"
                onClick={() =>
                  window.open("https://www.swmc.com/angelai/workshop", "_blank")
                }
              >
                Workshops with Live Experts
                <br />
                Delivered when YOU want it
                <br />
                Click Here to Request a Workshop!
                <br />
              </Button>
            </div>
          </Row>
          <Row className="workshop-details-section">
            <Col md={12} lg={6} sm={12}>
              <div className="workshop-text">
                <h6>Learn Hands-On with Expert</h6>
                <h1>WORKSHOPS</h1>
                <h3>Daily</h3>
                <p>
                  The workshops begin at the top of every hour,
                  <br />
                  from 10:00 A.M. - 5:00 P.M. local time,
                  <br />
                  on all business days.
                </p>
              </div>
            </Col>
            <Col md={12} lg={6} sm={12} className="workshopcard-section">
              <div className="workshopcard">
                <p className="opportunity">
                  You won't want to miss this opportunity to finance on demand
                  and embrace the rise of Ai. Our workshops bring speed,
                  certainty, confidence, and truth to your financial
                  commitments, allowing you to close fast and earn more.
                </p>
                <p className="power">
                  Tap into the Power of Ai with {links.current.brandName}
                  <br />
                  and Achieve More Than You Ever Imagined
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Suspense fallback={<div>Loading...</div>}>
        <WidgetScript />
      </Suspense>
    </>
  );
}
